<template>
  <div class="modern-form-container">
    <!-- Main Content Card -->
    <div class="form-card">
      <div class="card-header">
        <div class="header-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 21h18" />
            <path d="M5 21V7l8-4v18" />
            <path d="M19 21V11l-6-4" />
            <line x1="9" y1="9" x2="9" y2="9.01" />
            <line x1="9" y1="12" x2="9" y2="12.01" />
            <line x1="9" y1="15" x2="9" y2="15.01" />
            <line x1="15" y1="13" x2="15" y2="13.01" />
            <line x1="15" y1="16" x2="15" y2="16.01" />
          </svg>
        </div>
        <h2 class="form-title">
          {{ t('admin-ui.stepper_company_information') }}
        </h2>
        <p class="form-subtitle">
          {{
            t('admin-ui.company_form_verify_and_complete_your_company_details')
          }}
        </p>
      </div>

      <div class="card-body">
        <!-- VAT Warning if applicable -->
        <div
          v-if="!myObject.checkVatNumberBeforeAddAccount"
          class="warning-banner"
        >
          <div class="warning-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <span>{{ t('admin-ui.vat_number_not_checked') }}</span>
        </div>

        <hit-form
          name="CompanyInformationForm"
          :value="objectFormData"
          :validations="validations"
          :force-cancel-button-hidden="true"
          :force-save-button-hidden="true"
        >
          <template #default="{formData, validationState}">
            <div class="form-grid">
              <!-- Language Selection -->
              <div class="input-group full-width">
                <hit-select
                  name="userLanguageSystem"
                  class="required-field"
                  :value="formData.userLanguageSystem"
                  :label-fields="['designation']"
                  :options="HitLanguageUtils.getSupportedLanguageOptions()"
                  :label="t('admin-ui.language_system') + ' *'"
                  :validation-state="validationState.userLanguageSystem"
                  :filterable="false"
                  :clearable="false"
                  :placeholder="t('admin-ui.required')"
                  @change="onChangeUserLanguageSystem"
                />
              </div>

              <!-- VAT Number -->
              <div class="input-group">
                <hit-input
                  name="vatNumber"
                  class="required-field"
                  :value="formData.vatNumber || ''"
                  :validation-state="validationState.vatNumber"
                  :label="t('admin-ui.label_vat_number') + ' *'"
                  :placeholder="t('admin-ui.required')"
                  :disabled="true"
                  @input="onChangeVatNumber"
                />
                <div
                  v-if="myObject.checkVatNumberBeforeAddAccount"
                  class="input-status confirmed"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                  <span>{{ t('admin-ui.company-vat-verified') }}</span>
                </div>
                <div v-else class="input-status warning">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                    />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>{{ t('admin-ui.vat-number-not-verified') }}</span>
                </div>
              </div>

              <!-- Company Name -->
              <div class="input-group">
                <hit-input
                  name="designationLegal"
                  class="required-field"
                  :value="formData.designationLegal || ''"
                  :validation-state="validationState.designationLegal"
                  :label="t('admin-ui.label_legal_designation') + ' *'"
                  :placeholder="t('admin-ui.required')"
                  @input="onChangeDesignationLegal"
                />
              </div>

              <!-- Street -->
              <div class="input-group">
                <hit-input
                  name="street"
                  class="required-field"
                  :value="formData.street || ''"
                  :validation-state="validationState.street"
                  :label="t('admin-ui.label_street') + ' *'"
                  :placeholder="t('admin-ui.required')"
                  @input="onChangeStreet"
                />
              </div>

              <!-- House Number -->
              <div class="input-group">
                <hit-input
                  name="houseNumber"
                  class="required-field"
                  :value="formData.houseNumber || ''"
                  :validation-state="validationState.houseNumber"
                  :label="t('admin-ui.label_house_number') + ' *'"
                  :placeholder="t('admin-ui.required')"
                  @input="onChangeHouseNumber"
                />
              </div>

              <!-- Box Number -->
              <div class="input-group">
                <hit-input
                  name="boxNumber"
                  :value="formData.boxNumber || ''"
                  :validation-state="validationState.boxNumber"
                  :label="t('admin-ui.box_number')"
                  :placeholder="t('admin-ui.optional')"
                  @input="onChangeBoxNumber"
                />
              </div>

              <!-- Post Code -->
              <div class="input-group">
                <hit-input
                  name="postCode"
                  class="required-field"
                  :value="formData.postCode || ''"
                  :validation-state="validationState.postCode"
                  :label="t('admin-ui.label_post_code') + ' *'"
                  :placeholder="t('admin-ui.required')"
                  @input="onChangePostCode"
                />
              </div>

              <!-- City -->
              <div class="input-group">
                <hit-input
                  name="city"
                  class="required-field"
                  :value="formData.city || ''"
                  :validation-state="validationState.city"
                  :label="t('admin-ui.city') + ' *'"
                  :placeholder="t('admin-ui.required')"
                  @input="onChangeCity"
                />
              </div>

              <!-- Phone (optional) -->
              <div class="input-group">
                <hit-input
                  name="phone"
                  :value="formData.phone || ''"
                  :validation-state="validationState.phone"
                  :label="t('admin-ui.label_phone')"
                  :placeholder="t('admin-ui.optional')"
                  type="tel"
                  @input="onChangePhone"
                />
              </div>

              <!-- Mobile (required) -->
              <div class="input-group">
                <hit-input
                  name="mobile"
                  class="required-field"
                  :value="formData.mobile || ''"
                  :validation-state="validationState.mobile"
                  :label="t('admin-ui.mobile') + ' *'"
                  :placeholder="t('admin-ui.required')"
                  type="tel"
                  @input="onChangeMobile"
                />
              </div>

              <!-- Referral Code -->
              <div v-if="enableReferralSystem === 'true'" class="input-group">
                <hit-input
                  name="referralCode"
                  :value="formData.referralCode || ''"
                  :validation-state="validationState.referralCode"
                  :label="t('admin-ui.referral_code')"
                  :placeholder="t('admin-ui.optional')"
                  type="text"
                  @input="onChangeReferralCode"
                />
                <div v-if="objectFormData.referralCode" class="referral-status">
                  <div v-if="referralCodeValid" class="status-valid">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="20,6 9,17 4,12" />
                    </svg>
                    <span>
                      {{ t('admin-ui.referral-code-is-valid') }}
                      <template
                        v-if="freeMonths !== null && freeMonths !== undefined"
                      >
                        —
                        {{
                          t('admin-ui.referral-free-months', {
                            months: freeMonths,
                          })
                        }}
                      </template>
                    </span>
                  </div>
                  <div v-else class="status-invalid">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <span>{{ t('admin-ui.referral-code-is-invalid') }}</span>
                  </div>
                </div>
              </div>
              <!-- Activity -->
              <div class="input-group full-width">
                <hit-activity-list
                  v-model="objectFormData.activity"
                  :label="t('admin-ui.activity')"
                  :placeholder="t('admin-ui.optional')"
                  :language="formatLocale(locale)"
                />
              </div>
            </div>
          </template>
        </hit-form>
      </div>
    </div>

    <!-- Banner -->
    <div v-if="showBanner" class="banner-container">
      <hit-banner :show-animation="showAnimationBanner" @close="closeBanner()">
        <template #dynamic-content>
          <h6>{{ textBanner }}</h6>
        </template>
      </hit-banner>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from 'vue';
import {MyUserCompany} from '../../objects/UserCompany';
import {HitButton, HitForm, HitInput, HitPanel} from '@hit/components';
import {maxLength, minLength, required} from '@vuelidate/validators';
import {useI18n} from 'vue-i18n';
import HitSelect from '@hit/components/src/components/form/input/HitSelect.vue';
import TopBarNavigation from '../TopBarNavigation.vue';
import {HitLanguageUtils} from '@hit/base/src/utils';
import axios from 'axios';
import {Base64} from 'js-base64';
import HitBanner from '@hit/components/src/components/banner/HitBanner.vue';
import HitActivityList from '@hit/components/src/components/list/ActivityList.vue';

export default defineComponent({
  name: 'CompanyInformationsForm',
  components: {
    TopBarNavigation,
    HitSelect,
    HitButton,
    HitInput,
    HitForm,
    HitPanel,
    HitBanner,
    HitActivityList,
  },
  props: {
    myObject: {
      type: Object as PropType<MyUserCompany>,
      required: true,
    },
    triggerValidation: {
      type: Number,
      default: 0,
    },
    showBanner: {
      type: Boolean,
      default: false,
      required: true,
    },
    textBanner: {
      type: String,
      default: '',
      required: true,
    },
    showAnimationBanner: {
      type: Boolean,
      default: false,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['nextClick', 'previousClick', 'validationResult', 'close'],
  setup() {
    const {t, locale} = useI18n();
    const formatLocale = (loc: string) => {
      return loc.toLowerCase().replace('-', '_');
    };
    return {t, locale, formatLocale};
  },
  data() {
    return {
      objectFormData: {
        vatNumber: null as string | null,
        designationLegal: null as string | null,
        street: null as string | null,
        houseNumber: null as string | null,
        boxNumber: null as string | null,
        postCode: null as string | null,
        city: null as string | null,
        billingEmail: null as string | null,
        confirmBillingEmail: null as string | null,
        phone: null as string | null,
        mobile: null as string | null,
        userLanguageSystem: null as string | null,
        activity: [] as any[],
        referralCode: null as string | null,
      },
      referralCodeValid: true,
      freeMonths: null as number | null,
      mobileView: false,
      referralCodeExist: true,
      enableReferralSystem: process.env.VUE_APP_ENABLE_REFERRAL_SYSTEM,
      activityOptions: [] as {value: string; label: string}[],
    };
  },
  computed: {
    HitLanguageUtils() {
      return HitLanguageUtils;
    },
    validations() {
      return {
        vatNumber: {required},
        designationLegal: {required},
        street: {required},
        houseNumber: {required},
        postCode: {required},
        city: {required},
        mobile: {required},
        userLanguageSystem: {required},
        referralCode: {
          minLength: minLength(6),
          maxLength: maxLength(6),
        },
      };
    },
    allowNextClick() {
      return this.checkIfAllRequiredFieldsAreCompleted();
    },
  },
  watch: {
    triggerValidation(newVal, oldVal) {
      if (newVal > oldVal) {
        this.$nextTick(() => {
          this.validateAndContinue();
        });
      }
    },
  },
  async mounted() {
    // Initialize userLanguageSystem with current interface language if not already set
    if (this.myObject.userSystemLanguage) {
      this.objectFormData.userLanguageSystem = this.myObject.userSystemLanguage;
    } else {
      // Do not pre-fill with current interface language - force user to make conscious choice
      this.objectFormData.userLanguageSystem = null;
    }

    this.objectFormData.vatNumber = this.myObject.vatNumber
      ? this.myObject.vatNumber
      : null;
    this.objectFormData.designationLegal = this.myObject.designationLegal
      ? this.myObject.designationLegal
      : null;
    this.objectFormData.street = this.myObject.street
      ? this.myObject.street
      : null;
    this.objectFormData.houseNumber = this.myObject.houseNumber
      ? this.myObject.houseNumber
      : null;
    this.objectFormData.boxNumber = this.myObject.boxNumber
      ? this.myObject.boxNumber
      : null;
    this.objectFormData.postCode = this.myObject.postCode
      ? this.myObject.postCode
      : null;
    this.objectFormData.city = this.myObject.city ? this.myObject.city : null;
    this.objectFormData.billingEmail = this.myObject.billingEmail
      ? this.myObject.billingEmail
      : null;
    this.objectFormData.confirmBillingEmail = this.myObject.billingEmail
      ? this.myObject.billingEmail
      : null;
    this.objectFormData.phone = this.myObject.phone
      ? this.myObject.phone
      : null;
    this.objectFormData.mobile = this.myObject.mobile
      ? this.myObject.mobile
      : null;
    this.objectFormData.activity = this.myObject.activity
      ? JSON.parse(this.myObject.activity)
      : [];
    this.objectFormData.referralCode = this.myObject.referralCode
      ? this.myObject.referralCode
      : null;

    this.referralCodeValid = await this.checkIfReferralCodeIsValid(
      this.objectFormData.referralCode,
    );
  },
  methods: {
    generateAdminApiBase64Token(): string {
      const concatCredentials: string = `${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_USERNAME}:${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_PASSWORD}`;
      return Base64.encode(concatCredentials);
    },
    checkIfAllRequiredFieldsAreCompleted(): boolean {
      if (
        this.objectFormData.vatNumber !== null &&
        this.objectFormData.designationLegal !== null &&
        this.objectFormData.street !== null &&
        this.objectFormData.houseNumber &&
        this.objectFormData.postCode !== null &&
        this.objectFormData.city &&
        this.objectFormData.mobile !== null &&
        this.objectFormData.mobile !== '' &&
        this.objectFormData.userLanguageSystem !== null &&
        this.objectFormData.userLanguageSystem !== 'null' &&
        this.objectFormData.userLanguageSystem !== '' &&
        this.objectFormData.userLanguageSystem !== undefined
      ) {
        return true;
      } else {
        return false;
      }
    },
    getAllValues(): any {
      let myMap = new Map<string, string | null>();
      myMap.set('vatNumber', this.objectFormData.vatNumber);
      myMap.set('designationLegal', this.objectFormData.designationLegal);
      myMap.set('street', this.objectFormData.street);
      myMap.set('houseNumber', this.objectFormData.houseNumber);
      myMap.set('boxNumber', this.objectFormData.boxNumber);
      myMap.set('postCode', this.objectFormData.postCode);
      myMap.set('city', this.objectFormData.city);
      myMap.set('billingEmail', this.objectFormData.billingEmail);
      myMap.set('phone', this.objectFormData.phone);
      myMap.set('mobile', this.objectFormData.mobile);
      myMap.set('userLanguageSystem', this.objectFormData.userLanguageSystem);
      myMap.set('activity', JSON.stringify(this.objectFormData.activity));
      myMap.set('referralCode', this.objectFormData.referralCode);
      return myMap;
    },
    // Use to manage changes on form fields
    onChangeVatNumber(value: any) {
      this.objectFormData.vatNumber = value;
    },
    onChangeDesignationLegal(value: any) {
      this.objectFormData.designationLegal = value;
    },
    onChangeStreet(value: any) {
      this.objectFormData.street = value;
    },
    onChangeHouseNumber(value: any) {
      this.objectFormData.houseNumber = value;
    },
    onChangeBoxNumber(value: any) {
      this.objectFormData.boxNumber = value;
    },
    onChangePostCode(value: any) {
      this.objectFormData.postCode = value;
    },
    onChangeCity(value: any) {
      this.objectFormData.city = value;
    },
    onChangePhone(value: any) {
      this.objectFormData.phone = value;
    },
    onChangeMobile(value: any) {
      this.objectFormData.mobile = value;
    },
    async onChangeReferralCode(value: any) {
      this.objectFormData.referralCode = value;
      const result = await this.checkIfReferralCodeIsValid(value);
      this.referralCodeValid = result;
    },
    async checkIfReferralCodeIsValid(referralCode: any) {
      if (!referralCode) {
        this.freeMonths = null;
        return true;
      }

      if (referralCode.length !== 6) {
        this.freeMonths = null;
        return false;
      }

      const token = this.generateAdminApiBase64Token();

      const config = {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        params: {
          referral_code: referralCode,
        },
      };

      const route = 'referral/check';
      const url = process.env.VUE_APP_ADMIN_API_BASE_URL + route;

      try {
        const response = await axios.get(url, config);
        // New response structure: { valid: boolean, freeMonths: number | null }
        if (typeof response.data === 'boolean') {
          // Backward compatibility if backend not updated yet
          this.freeMonths = response.data ? 2 : null;
          return response.data;
        }
        this.freeMonths =
          response.data && typeof response.data.freeMonths === 'number'
            ? response.data.freeMonths
            : null;
        return !!(response.data && response.data.valid);
      } catch (error) {
        console.error(
          'Erreur lors de la vérification du code de parrainage',
          error,
        );
        this.freeMonths = null;
        return false;
      }
    },
    onChangeUserLanguageSystem(value: any) {
      this.objectFormData.userLanguageSystem = value;
    },
    validateAndContinue() {
      // Check required fields individually
      let missingFields = [];

      if (!this.objectFormData.vatNumber) {
        missingFields.push(this.t('admin-ui.vat_number').toLowerCase());
      }
      if (!this.objectFormData.designationLegal) {
        missingFields.push(
          this.t('admin-ui.label_legal_designation').toLowerCase(),
        );
      }
      if (!this.objectFormData.street) {
        missingFields.push(this.t('admin-ui.label_street').toLowerCase());
      }
      if (!this.objectFormData.houseNumber) {
        missingFields.push(this.t('admin-ui.label_house_number').toLowerCase());
      }
      if (!this.objectFormData.postCode) {
        missingFields.push(this.t('admin-ui.label_post_code').toLowerCase());
      }
      if (!this.objectFormData.city) {
        missingFields.push(this.t('admin-ui.city').toLowerCase());
      }
      if (!this.objectFormData.mobile) {
        missingFields.push(this.t('admin-ui.mobile').toLowerCase());
      }
      if (
        !this.objectFormData.userLanguageSystem ||
        this.objectFormData.userLanguageSystem === '' ||
        this.objectFormData.userLanguageSystem === 'null'
      ) {
        missingFields.push(this.t('admin-ui.language_system').toLowerCase());
      }

      // If any required fields are missing
      if (missingFields.length > 0) {
        let message;
        if (missingFields.length === 1) {
          message = `${this.t('admin-ui.please-fill-your')} ${
            missingFields[0]
          }.`;
        } else if (missingFields.length === 2) {
          message = `${this.t(
            'admin-ui.please-fill-your',
          )} ${missingFields.join(` ${this.t('admin-ui.and')} `)}.`;
        } else {
          const lastField = missingFields.pop();
          message = `${this.t(
            'admin-ui.please-fill-your',
          )} ${missingFields.join(', ')} ${this.t(
            'admin-ui.and',
          )} ${lastField}.`;
        }

        this.$emit('validationResult', {
          success: false,
          message: message,
        });
        return;
      }

      // Check referral code validity if provided
      if (this.objectFormData.referralCode && !this.referralCodeValid) {
        this.$emit('validationResult', {
          success: false,
          message: this.t('admin-ui.referral-code-is-invalid'),
        });
        return;
      }

      // All validation passed, emit nextClick to trigger actual form submission
      this.$emit('nextClick', this.getAllValues());
      this.$emit('validationResult', {success: true});
    },
    closeBanner() {
      this.$emit('close');
    },
  },
});
</script>

<style scoped>
.modern-form-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 3rem 1rem 1rem 1rem;
  background: #f1f5f9;
  position: relative;
}

.form-card {
  background: #ffffff;
  border-radius: 1rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 0;
  max-width: 600px;
  width: 100%;
  overflow: hidden;
  position: relative;
  border: 1px solid #e5e7eb;
}

.card-header {
  background: #0d3749;
  color: white;
  padding: 1.5rem;
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  margin-bottom: 0.75rem;
}

.form-title {
  font-size: 1.375rem;
  font-weight: 600;
  margin: 0 0 0.375rem 0;
  line-height: 1.3;
}

.form-subtitle {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
}

.card-body {
  padding: 1.5rem;
}

.warning-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #fef3cd 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 1rem;
  margin-bottom: 2rem;
  color: #92400e;
  font-weight: 500;
  box-shadow: 0 4px 6px rgba(245, 158, 11, 0.1);
  font-size: 0.875rem;
  line-height: 1.4;
}

.warning-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f59e0b;
  border-radius: 50%;
  color: white;
  min-width: 28px;
  min-height: 28px;
}

.warning-icon svg {
  width: 16px;
  height: 16px;
  stroke-width: 2.5;
}

.form-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr 1fr;
}

.input-group {
  position: relative;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.input-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.input-status.confirmed {
  color: #059669;
}

.input-status.warning {
  color: #f59e0b;
}

.input-status svg {
  flex-shrink: 0;
}

.referral-status {
  margin-top: 0.75rem;
}

.status-valid,
.status-invalid {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
}

.status-valid {
  color: #059669;
  background: rgba(5, 150, 105, 0.1);
  border: 1px solid rgba(5, 150, 105, 0.2);
}

.status-invalid {
  color: #dc2626;
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.2);
}

.status-valid svg,
.status-invalid svg {
  flex-shrink: 0;
}

/* Placeholder styling for required text inputs */
:deep(.required-field input::placeholder) {
  color: #dc2626;
  font-style: italic;
  opacity: 0.7;
}

/* Responsive Design */
@media (min-width: 768px) {
  .modern-form-container {
    padding: 4rem 1rem 1rem 1rem;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .form-subtitle {
    font-size: 1rem;
  }

  .card-header {
    padding: 1.75rem;
  }

  .card-body {
    padding: 1.75rem;
  }
}

@media (max-width: 768px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.5rem 1rem 0.25rem 1rem;
    justify-content: center;
  }

  .form-card {
    max-width: 100%;
    border-radius: 0.75rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.25rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .warning-banner {
    padding: 0.875rem 1rem;
    margin-bottom: 1rem;
  }
}

@media (max-width: 480px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.25rem 0.75rem 0.125rem 0.75rem;
  }

  .form-card {
    border-radius: 0.5rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.125rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .warning-banner {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }
}

.banner-container {
  margin-top: 1rem;
  max-width: 600px;
  width: 100%;
}
</style>
